import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  createFundingRequest,
  CreateFundingRequestBody,
  DashboardRange,
  getDashboardActivity,
  getDashboardCapital,
  getDashboardFundingHistory,
  getDashboardMemberMix,
  getDashboardVisibility,
  getFundingRequests,
  getGuestFlow,
  GuestFlowOverview,
  resetLocationsGuestFlow,
  SaveGuestFlowBody,
  saveLocationsGuestFlow,
  saveMerchantGuestFlow,
} from '@/api/dashboard.api'
import { useActiveLocationsStore } from '../useActiveLocationsStore'

const FUNDING_REQUESTS_QUERY_KEY = ['dashboard', 'funding-requests']
const GUEST_FLOW_QUERY_KEY = ['dashboard', 'guest-flow']

const useActiveLocationsKey = () =>
  useActiveLocationsStore((state) => [...state.activeLocationIds].sort().join(','))

/**
 * Pages and Overview metrics the Hub hid for this merchant. Merchant-wide, so not keyed by the
 * location selection; lives under ['dashboard'] so dashboard-wide invalidation refreshes it too.
 */
export const useDashboardVisibilityQuery = ({ enabled = true }: { enabled?: boolean } = {}) =>
  useQuery({
    queryKey: ['dashboard', 'visibility'],
    queryFn: async () => (await getDashboardVisibility()).data,
    enabled,
  })

export const useDashboardCapital = ({ enabled = true }: { enabled?: boolean } = {}) => {
  const locationsKey = useActiveLocationsKey()
  return useQuery({
    queryKey: ['dashboard', 'capital', locationsKey],
    queryFn: async () => (await getDashboardCapital()).data,
    enabled,
  })
}

export const useDashboardActivity = (range: DashboardRange) => {
  const locationsKey = useActiveLocationsKey()
  return useQuery({
    queryKey: ['dashboard', 'activity', locationsKey, range.from, range.to],
    queryFn: async () => (await getDashboardActivity(range)).data,
    placeholderData: keepPreviousData,
  })
}

export const useDashboardMemberMix = (range: DashboardRange, { enabled = true }: { enabled?: boolean } = {}) => {
  const locationsKey = useActiveLocationsKey()
  return useQuery({
    queryKey: ['dashboard', 'members', locationsKey, range.from, range.to],
    queryFn: async () => (await getDashboardMemberMix(range)).data,
    placeholderData: keepPreviousData,
    enabled,
  })
}

export const useDashboardFundingHistory = () => {
  const locationsKey = useActiveLocationsKey()
  return useQuery({
    queryKey: ['dashboard', 'funding-history', locationsKey],
    queryFn: async () => (await getDashboardFundingHistory()).data,
  })
}

/** Past funding requests across the user's locations; not tied to the top-bar location selection. */
export const useFundingRequests = (enabled = true) =>
  useQuery({
    queryKey: FUNDING_REQUESTS_QUERY_KEY,
    queryFn: async () => (await getFundingRequests()).data,
    enabled,
  })

/** Submits a funding request and refreshes the past-requests list. */
export const useCreateFundingRequestMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (body: CreateFundingRequestBody) => (await createFundingRequest(body)).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: FUNDING_REQUESTS_QUERY_KEY }),
  })
}

/** The all-locations plan and every location of the user with the plan it follows. */
export const useGuestFlow = () =>
  useQuery({
    queryKey: GUEST_FLOW_QUERY_KEY,
    queryFn: async () => (await getGuestFlow()).data,
  })

export type GuestFlowSaveTarget = { scope: 'merchant' } | { scope: 'locations'; locationIds: string[] }

/** Saves the all-locations plan or the given locations' own plans; the response is the whole page state. */
export const useSaveGuestFlowMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ target, body }: { target: GuestFlowSaveTarget; body: SaveGuestFlowBody }) =>
      target.scope === 'merchant'
        ? (await saveMerchantGuestFlow(body)).data
        : (await saveLocationsGuestFlow(target.locationIds, body)).data,
    onSuccess: (overview) => queryClient.setQueryData<GuestFlowOverview>(GUEST_FLOW_QUERY_KEY, overview),
  })
}

/** Puts locations back on the all-locations plan. */
export const useResetGuestFlowMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (locationIds: string[]) => (await resetLocationsGuestFlow(locationIds)).data,
    onSuccess: (overview) => queryClient.setQueryData<GuestFlowOverview>(GUEST_FLOW_QUERY_KEY, overview),
  })
}
